import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ProductOptionsDialog } from '../components/productSettings';
import { api } from '../api/client';
import type { Product, ProductCredentials } from '../api/types';
import { IconKey, IconPlus, IconProducts, IconRefresh } from '../components/icons';
import { Pill } from '../components/license';
import {
  Button,
  Callout,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorNote,
  SecretReveal,
  SkeletonRows,
  TextAreaField,
  TextField,
  useToast,
  CopyButton,
} from '../components/ui';
import { useCapabilities } from '../lib/auth';
import { formatDate } from '../lib/license';
import { slugify } from './Companies';

/**
 * The websites you sell. Each one gets an API key and secret its BACKEND uses
 * to talk to Heron — never its frontend, since a browser must not hold the
 * secret.
 */
export function ProductsPage() {
  const [creating, setCreating] = useState(false);
  const [credentials, setCredentials] = useState<{ product: string; creds: ProductCredentials } | null>(
    null,
  );

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ products: Product[] }>('/api/staff/products'),
  });

  const products = data?.products ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Products</h1>
          <p className="page-head__sub">
            Each product’s backend authenticates to Heron with a key and secret. Adding a product
            here is all it takes to bring a new website under licensing.
          </p>
        </div>
        <div className="page-head__actions">
          <Button variant="primary" icon={<IconPlus />} onClick={() => setCreating(true)}>
            New product
          </Button>
        </div>
      </div>

      <section className="panel">
        {isPending && <SkeletonRows rows={3} />}
        {isError && (
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        )}

        {!isPending && !isError && products.length === 0 && (
          <EmptyState
            icon={<IconProducts size={30} />}
            title="No products yet"
            body="Register your first website. You will get a key and secret to put in its backend configuration, and it can then ask Heron whether a login is allowed."
            action={
              <Button variant="primary" icon={<IconPlus />} onClick={() => setCreating(true)}>
                New product
              </Button>
            }
          />
        )}

        {products.length > 0 && (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Code</th>
                  <th>API key</th>
                  <th>Licenses</th>
                  <th>Status</th>
                  <th>Added</th>
                  <th className="table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-strong">{product.name}</span>
                        {product.description && (
                          <span className="cell-meta">{product.description}</span>
                        )}
                      </div>
                    </td>
                    <td className="cell-mono">{product.code}</td>
                    <td>
                      <span className="row" style={{ gap: 'var(--s1)' }}>
                        <code className="cell-mono">{product.api_key}</code>
                        <CopyButton value={product.api_key} label="API key copied" />
                      </span>
                    </td>
                    <td className="table__num">{product.license_count ?? 0}</td>
                    <td>
                      {product.is_active ? (
                        <Pill tone="live">Active</Pill>
                      ) : (
                        <Pill tone="expired">Disabled</Pill>
                      )}
                    </td>
                    <td className="cell-meta tabular">{formatDate(product.created_at)}</td>
                    <td className="table__actions">
                      <ProductActions
                        product={product}
                        onRotated={(creds) =>
                          setCredentials({ product: product.name, creds })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CreateProductDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(name, creds) => setCredentials({ product: name, creds })}
      />

      {credentials && (
        <Dialog
          open
          onClose={() => setCredentials(null)}
          title={`Credentials for ${credentials.product}`}
          wide
        >
          <div className="stack">
            <Callout tone="warn" title="The secret is shown once">
              Heron stores only a hash of it. Put both values into the product’s backend
              configuration now — if you lose the secret you must rotate, which breaks the product
              until its config is updated.
            </Callout>

            <SecretReveal label="X-Heron-Product-Key" value={credentials.creds.apiKey} />
            <SecretReveal label="X-Heron-Product-Secret" value={credentials.creds.apiSecret} />

            <Callout tone="danger" title="Backend only">
              These headers identify the product’s server, not a user. Never ship them to a
              browser — a leaked secret lets anyone ask Heron to authenticate on your behalf.
            </Callout>

            <div className="form-actions">
              <Button variant="primary" onClick={() => setCredentials(null)}>
                I’ve stored them
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

function ProductActions({
  product,
  onRotated,
}: {
  product: Product;
  onRotated: (creds: ProductCredentials) => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const can = useCapabilities();
  const [rotating, setRotating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [settingSecret, setSettingSecret] = useState(false);
  const [secret, setSecret] = useState('');

  const remove = useMutation({
    mutationFn: () => api.del(`/api/staff/products/${product.id}`, { confirmCode: product.code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setDeleting(false);
      push({ tone: 'success', title: `${product.name} deleted` });
    },
  });

  const setSecretMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/staff/products/${product.id}/set-secret`, { apiSecret: secret }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      setSettingSecret(false);
      setSecret('');
      push({
        tone: 'success',
        title: 'Secret updated',
        body: `${product.name} now accepts the value you entered.`,
      });
    },
  });

  const toggle = useMutation({
    mutationFn: (isActive: boolean) =>
      api.patch(`/api/staff/products/${product.id}`, { isActive }),
    onSuccess: (_data, isActive) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      push({
        tone: 'success',
        title: isActive ? `${product.name} enabled` : `${product.name} disabled`,
      });
    },
  });

  const rotate = useMutation({
    mutationFn: () =>
      api.post<{ credentials: ProductCredentials }>(
        `/api/staff/products/${product.id}/rotate-secret`,
      ),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      setRotating(false);
      onRotated(data.credentials);
    },
  });

  return (
    <>
      <span className="row" style={{ gap: 'var(--s2)', justifyContent: 'flex-end' }}>
        <Button
          size="sm"
          loading={toggle.isPending}
          onClick={() => toggle.mutate(!product.is_active)}
        >
          {product.is_active ? 'Disable' : 'Enable'}
        </Button>
        <Button
          size="sm"
          title="Per-company switches this product reads, such as showing cost figures"
          onClick={() => setOptionsOpen(true)}
        >
          Options
        </Button>
        <Button
          size="sm"
          icon={<IconKey size={14} />}
          onClick={() => {
            setSecret('');
            setSettingSecret(true);
          }}
        >
          Set secret
        </Button>
        <Button
          size="sm"
          variant="danger"
          icon={<IconRefresh size={14} />}
          onClick={() => setRotating(true)}
        >
          Rotate
        </Button>
        {can.canDestroy && (
          <Button
            size="sm"
            variant="danger"
            title="Delete this product. Only possible once it has no live licences."
            onClick={() => setDeleting(true)}
          >
            Delete
          </Button>
        )}
      </span>

      <ProductOptionsDialog
        productId={product.id}
        productName={product.name}
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
      />

      <Dialog
        open={settingSecret}
        onClose={() => {
          setSecretMutation.reset();
          setSettingSecret(false);
        }}
        title={`Set ${product.name}’s secret`}
        hint="Enter the value this product already has in its environment. Nothing in the product needs to change — only the key, which you can copy from the table."
      >
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            setSecretMutation.mutate();
          }}
        >
          {setSecretMutation.isError && <ErrorNote error={setSecretMutation.error} />}

          <Callout tone="warn" title="The current secret stops working">
            Whatever {product.name} is using now is replaced the moment you save. If the value you
            type does not match its configuration, every sign-in through it fails with{' '}
            <code className="mono">PRODUCT_CREDENTIALS_INVALID</code>.
          </Callout>

          <TextField
            label="HERON_PRODUCT_SECRET"
            value={secret}
            required
            mono
            autoFocus
            autoComplete="off"
            spellCheck={false}
            hint="At least 24 characters, no spaces. Paste it from the product’s .env."
            onChange={(event) => setSecret(event.currentTarget.value)}
          />

          <SecretReveal label="X-Heron-Product-Key (unchanged)" value={product.api_key} />

          <div className="form-actions">
            <Button onClick={() => setSettingSecret(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="primary"
              disabled={secret.trim().length < 24}
              loading={setSecretMutation.isPending}
            >
              Save secret
            </Button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={rotating}
        onClose={() => setRotating(false)}
        onConfirm={() => rotate.mutate()}
        title={`Rotate ${product.name}’s secret?`}
        body={`This issues a NEW random key and secret. The current pair stops working instantly, and ${product.name} cannot authenticate anyone until both values are updated in its configuration. If you only want to re-enter a secret you already have, use "Set secret" instead.`}
        confirmLabel="Rotate secret"
        confirmWord={product.code}
        loading={rotate.isPending}
        error={rotate.error}
      />

      <ConfirmDialog
        open={deleting}
        onClose={() => {
          remove.reset();
          setDeleting(false);
        }}
        onConfirm={() => remove.mutate()}
        title={`Delete ${product.name}?`}
        body={
          <>
            This removes the product permanently. It is only possible once the product has no live
            licences — if any company still holds one, revoke its licences first. Any already-revoked
            licences are cleared as part of the deletion.
          </>
        }
        confirmLabel="Delete product"
        confirmWord={product.code}
        loading={remove.isPending}
        error={remove.error}
      />
    </>
  );
}

function CreateProductDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string, creds: ProductCredentials) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ product: Product; credentials: ProductCredentials }>('/api/staff/products', {
        name,
        code,
        description: description || undefined,
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setName('');
      setCode('');
      setCodeEdited(false);
      setDescription('');
      onClose();
      onCreated(data.product.name, data.credentials);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New product"
      hint="The code becomes the audience claim inside the tokens Heron issues for this product, so it should not change once the product is live."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <ErrorNote error={mutation.error} />}

        <TextField
          label="Name"
          value={name}
          required
          autoFocus
          placeholder="Product A"
          onChange={(event) => {
            const value = event.currentTarget.value;
            setName(value);
            if (!codeEdited) setCode(slugify(value));
          }}
        />

        <TextField
          label="Code"
          value={code}
          required
          mono
          hint="Lowercase letters, digits and hyphens. Fixed once the product ships."
          onChange={(event) => {
            setCodeEdited(true);
            setCode(event.currentTarget.value);
          }}
        />

        <TextAreaField
          label="Description"
          optional
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
        />

        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Create product
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
